// P-MILESTONE-DERIVATION — day-end celebration "shown once per round" latch.
//
// The day-end story celebration is auto-navigated from /play when a story
// can be unlocked and hasn't been shown for THIS claim opportunity yet
// (PathHome gate, PART B/D). Backing out of the celebration WITHOUT picking
// a story must not re-trap the kid on the celebration every time they
// return to /play — so the gate also checks this localStorage latch: once
// the celebration has been shown for a given round, the auto-navigation
// stops (the kid still reaches it via the reminder card, and a real pick
// writes the 'story_unlocked' event that independently closes the gate).
//
// MA-165 (round-based book economy — unlimited unlocks/day): the latch is
// ROUND-SCOPED, not just day-scoped — `pw.celebration.shown.<date>#<round>`,
// where <round> is trailState.pendingRoundSlot's "Nth claim opportunity"
// counter. Combining the date component (still present in the key) with the
// round component means a same-day round 2 gets a FRESH key (new round
// number, same date) — its auto-nav is not suppressed by round 1's latch —
// and a round carried over across midnight also gets a fresh key (same
// round number, new date), so crossing a day boundary doesn't re-trap OR
// wrongly re-arm a kid who backed out of round 1's celebration yesterday.
//
// MA-378: the key is also CHILD-SCOPED — `pw.celebration.shown.<childId>.
// <date>#<round>` — keyed on the LOCAL child id (activeChildId), NOT the
// async cloud id, same convention as readerNarrationHintLatch.ts /
// tapAWordHintLatch.ts. Without this, two siblings on one shared device who
// both complete round N the same day collide on the SAME date+round key:
// sibling A reaching (or backing out of) the celebration latches it, which
// then silently suppresses sibling B's auto-navigation to their own
// celebration for that round later the same day (B still reaches the picker
// manually via the reminder card, but the peak "auto-navigated to your
// reward" moment is dampened for the second sibling every day). A falsy
// childId (no local child resolved yet) is treated as "already shown" on
// read (never auto-navigate with nothing to latch to) and is a no-op on
// write — mirroring the hint latches' safety direction.
//
// localDayKey is pure (unit-tested); the read/write wrappers swallow storage
// errors (private mode / disabled storage) and degrade to "not shown",
// which is harmless — the celebration screen marks itself shown on mount and
// is idempotent, so at worst the gate re-navigates once.

/** Local calendar day as YYYY-MM-DD (device timezone). Pure + testable. */
export function localDayKey(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** The localStorage key the latch is stored under for this (locally
 *  identified) child, `now`'s local day, AND the given round
 *  (trailState.pendingRoundSlot's claim-opportunity id). */
export function celebrationShownStorageKeyForRound(
  childId: string,
  round: number,
  now: Date = new Date(),
): string {
  return `pw.celebration.shown.${childId}.${localDayKey(now)}#${round}`;
}

/** True iff the celebration has already been shown to this child for this
 *  round today (latch set). A falsy childId (no local child resolved yet)
 *  is treated as "already shown" — never auto-navigate with nothing to
 *  latch it to. */
export function wasCelebrationShownForRound(
  childId: string | null | undefined,
  round: number,
  now: Date = new Date(),
): boolean {
  if (!childId) return true;
  try {
    return localStorage.getItem(celebrationShownStorageKeyForRound(childId, round, now)) === "1";
  } catch {
    return false;
  }
}

/** Set the latch for this child + round. Called by the celebration screen on
 *  mount so however it was reached (auto-nav or reminder card), re-navigation
 *  for THIS child+round stops — a later round (new pendingRoundSlot) or a
 *  different child (a sibling on the same device) gets its own key. A falsy
 *  childId is a no-op — nothing to latch. */
export function markCelebrationShownForRound(
  childId: string | null | undefined,
  round: number,
  now: Date = new Date(),
): void {
  if (!childId) return;
  try {
    localStorage.setItem(celebrationShownStorageKeyForRound(childId, round, now), "1");
  } catch {
    // ignore — see module header (degrades to a harmless re-navigation).
  }
}
